import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { enrichBusinessData } from '../services/geminiProxyService';
import { exportToCsv } from '../utils/csvExporter';
import { API_BASE_URL } from '../config/api';
import type { Business } from '../types';

interface LLCRecord {
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  website?: string;
  email?: string;
  queryStrength?: 'high' | 'medium' | 'low';
  enrichmentStatus?: 'pending' | 'processing' | 'completed' | 'failed';
  enrichedData?: Partial<Business>;
}

export default function LLCEnrichment() {
  const { user } = useAuth();

  const [llcRecords, setLlcRecords] = useState<LLCRecord[]>([]);
  const [savedEnrichments, setSavedEnrichments] = useState<any[]>([]);
  const [currentEnrichmentId, setCurrentEnrichmentId] = useState<number | null>(null);
  const [currentEnrichmentName, setCurrentEnrichmentName] = useState<string>('');
  const [selectedRecords, setSelectedRecords] = useState<Set<number>>(new Set());
  const [isUploading, setIsUploading] = useState(false);
  const [isEnriching, setIsEnriching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [enrichmentProgress, setEnrichmentProgress] = useState({ current: 0, total: 0 });

  // Load saved enrichments on mount
  useEffect(() => {
    loadSavedEnrichments();
  }, []);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setError(null);

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());

      if (lines.length === 0) {
        throw new Error('CSV file is empty');
      }

      // Parse CSV header
      const header = lines[0].split(',').map(h => h.trim().toLowerCase());

      // Find column indices
      const nameIdx = header.findIndex(h => h.includes('name') || h.includes('business') || h.includes('llc'));
      const addressIdx = header.findIndex(h => h.includes('address') && !h.includes('email'));
      const cityIdx = header.findIndex(h => h.includes('city'));
      const stateIdx = header.findIndex(h => h.includes('state'));
      const zipIdx = header.findIndex(h => h.includes('zip'));
      const phoneIdx = header.findIndex(h => h.includes('phone'));
      const websiteIdx = header.findIndex(h => h.includes('website') || h.includes('url'));
      const emailIdx = header.findIndex(h => h.includes('email'));

      if (nameIdx === -1) {
        throw new Error('CSV must contain a "name" column');
      }

      // Parse records
      const records: LLCRecord[] = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/^["']|["']$/g, ''));

        const record: LLCRecord = {
          name: values[nameIdx] || '',
          address: addressIdx !== -1 ? values[addressIdx] : undefined,
          city: cityIdx !== -1 ? values[cityIdx] : undefined,
          state: stateIdx !== -1 ? values[stateIdx]?.toUpperCase() : undefined,
          zip: zipIdx !== -1 ? values[zipIdx] : undefined,
          phone: phoneIdx !== -1 ? values[phoneIdx] : undefined,
          website: websiteIdx !== -1 ? values[websiteIdx] : undefined,
          email: emailIdx !== -1 ? values[emailIdx] : undefined,
          enrichmentStatus: 'pending',
          queryStrength: calculateQueryStrength(values, { nameIdx, addressIdx, cityIdx, stateIdx }),
        };

        return record;
      }).filter(r => r.name);

      setLlcRecords(records);
      setSelectedRecords(new Set()); // Clear selection on new upload
      setSuccessMessage(`Loaded ${records.length} LLC records from CSV`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to parse CSV file');
    } finally {
      setIsUploading(false);
    }
  };

  const toggleRecordSelection = (index: number) => {
    const newSelection = new Set(selectedRecords);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    setSelectedRecords(newSelection);
  };

  const selectAllRecords = () => {
    setSelectedRecords(new Set(llcRecords.map((_, idx) => idx)));
  };

  const deselectAllRecords = () => {
    setSelectedRecords(new Set());
  };

  const selectByQueryStrength = (strength: 'high' | 'medium' | 'low') => {
    const indices = llcRecords
      .map((record, idx) => ({ record, idx }))
      .filter(({ record }) => record.queryStrength === strength)
      .map(({ idx }) => idx);
    setSelectedRecords(new Set(indices));
  };

  const loadSavedEnrichments = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/llc-enrichments`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to load saved enrichments');

      const data = await response.json();
      setSavedEnrichments(data.enrichments || []);
    } catch (err: any) {
      console.error('Failed to load saved enrichments:', err);
    }
  };

  const loadEnrichment = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/llc-enrichments/${id}`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to load enrichment');

      const data = await response.json();
      setLlcRecords(data.enrichment.records_data);
      setCurrentEnrichmentId(id);
      setCurrentEnrichmentName(data.enrichment.name);
      setSelectedRecords(new Set()); // Clear selection when loading
      setSuccessMessage(`Loaded "${data.enrichment.name}" with ${data.enrichment.total_records} records`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to load enrichment');
    }
  };

  const saveEnrichment = async () => {
    if (llcRecords.length === 0) {
      setError('No records to save');
      return;
    }

    const name = prompt('Enter a name for this enrichment:', currentEnrichmentName || `LLC Enrichment ${new Date().toLocaleDateString()}`);
    if (!name || !name.trim()) return;

    setIsSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/llc-enrichments`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          records: llcRecords,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save enrichment');
      }

      const data = await response.json();
      setCurrentEnrichmentId(data.enrichment.id);
      setCurrentEnrichmentName(name.trim());
      setSuccessMessage('Enrichment saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadSavedEnrichments();
    } catch (err: any) {
      setError(err.message || 'Failed to save enrichment');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteEnrichment = async (id: number) => {
    if (!confirm('Are you sure you want to delete this enrichment?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/llc-enrichments/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to delete enrichment');

      setSuccessMessage('Enrichment deleted successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);

      // If we deleted the current enrichment, clear it
      if (currentEnrichmentId === id) {
        setLlcRecords([]);
        setCurrentEnrichmentId(null);
        setCurrentEnrichmentName('');
      }

      await loadSavedEnrichments();
    } catch (err: any) {
      setError(err.message || 'Failed to delete enrichment');
    }
  };

  const calculateQueryStrength = (
    values: string[],
    indices: { nameIdx: number; addressIdx: number; cityIdx: number; stateIdx: number }
  ): 'high' | 'medium' | 'low' => {
    let score = 0;

    // Name exists (required)
    if (values[indices.nameIdx]) score += 1;

    // Has address
    if (indices.addressIdx !== -1 && values[indices.addressIdx]) score += 2;

    // Has city
    if (indices.cityIdx !== -1 && values[indices.cityIdx]) score += 1;

    // Has state
    if (indices.stateIdx !== -1 && values[indices.stateIdx]) score += 1;

    if (score >= 4) return 'high';
    if (score >= 2) return 'medium';
    return 'low';
  };

  const handleEnrichSelected = async () => {
    if (selectedRecords.size === 0) {
      setError('Please select at least one record to enrich.');
      return;
    }

    setIsEnriching(true);
    setError(null);

    const selectedIndices = Array.from(selectedRecords);
    setEnrichmentProgress({ current: 0, total: selectedIndices.length });

    const enrichmentOptions = ['website', 'email', 'phone', 'instagram', 'facebook', 'linkedin'];

    try {
      const enrichedRecords = [...llcRecords];
      let processedCount = 0;

      for (const i of selectedIndices) {
        const record = enrichedRecords[i];

        // Update status to processing
        enrichedRecords[i] = { ...record, enrichmentStatus: 'processing' };
        setLlcRecords([...enrichedRecords]);

        try {
          // Create a Business object for enrichment
          const businessForEnrichment: Business = {
            id: i,
            name: record.name,
            address: record.address || `${record.city || ''}, ${record.state || ''}`.trim(),
            phone: record.phone,
            website: record.website,
            rating: 0,
            userRatingsTotal: 0,
            types: [],
            placeId: '',
          };

          const enrichedData = await enrichBusinessData(businessForEnrichment, enrichmentOptions);

          enrichedRecords[i] = {
            ...record,
            enrichmentStatus: 'completed',
            enrichedData,
          };
        } catch (err) {
          console.error(`Failed to enrich ${record.name}:`, err);
          enrichedRecords[i] = { ...record, enrichmentStatus: 'failed' };
        }

        processedCount++;
        setEnrichmentProgress({ current: processedCount, total: selectedIndices.length });
        setLlcRecords([...enrichedRecords]);
      }

      const completedCount = selectedIndices.filter(i => enrichedRecords[i].enrichmentStatus === 'completed').length;
      setSuccessMessage(`Successfully enriched ${completedCount} out of ${selectedIndices.length} selected records`);
      setTimeout(() => setSuccessMessage(null), 3000);

      // Auto-save after enrichment completes
      if (completedCount > 0) {
        await saveEnrichment();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to enrich records');
    } finally {
      setIsEnriching(false);
      setEnrichmentProgress({ current: 0, total: 0 });
    }
  };

  const handleExport = () => {
    if (llcRecords.length === 0) {
      setError('No records to export');
      return;
    }

    // Convert to Business format for exportToCsv (uses lowercase properties)
    const exportData = llcRecords.map(record => ({
      name: record.name,
      address: record.address || `${record.city || ''}, ${record.state || ''}`.trim(),
      phone: record.enrichedData?.phone || record.phone || '',
      website: record.enrichedData?.website || record.website || '',
      email: record.enrichedData?.email || record.email || '',
      instagram: record.enrichedData?.instagram || '',
      facebook: record.enrichedData?.facebook || '',
      linkedin: record.enrichedData?.linkedin || '',
      x: record.enrichedData?.x || '',
      yelp: record.enrichedData?.yelp || '',
    }));

    exportToCsv(exportData as Business[], `llc-enrichment-${Date.now()}.csv`);
    setSuccessMessage('Exported enriched data successfully!');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const getStateStats = () => {
    const states = new Map<string, number>();
    llcRecords.forEach(record => {
      if (record.state) {
        states.set(record.state, (states.get(record.state) || 0) + 1);
      }
    });
    return Array.from(states.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
  };

  const getQueryStrengthStats = () => {
    const stats = { high: 0, medium: 0, low: 0 };
    llcRecords.forEach(record => {
      if (record.queryStrength) {
        stats[record.queryStrength]++;
      }
    });
    return stats;
  };

  const getEnrichmentStats = () => {
    const stats = { pending: 0, processing: 0, completed: 0, failed: 0 };
    llcRecords.forEach(record => {
      if (record.enrichmentStatus) {
        stats[record.enrichmentStatus]++;
      }
    });
    return stats;
  };

  const stateStats = getStateStats();
  const queryStrengthStats = getQueryStrengthStats();
  const enrichmentStats = getEnrichmentStats();

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold text-white mb-2">LLC Data Enrichment</h1>
          <p className="text-purple-50">
            Upload a CSV file with LLC information to enrich with website, email, social media, and more
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-900/50 border border-green-600 rounded-lg p-4 text-green-200">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/50 border border-red-600 rounded-lg p-4 text-red-200">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* Upload Section */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold text-slate-100 mb-4">Upload LLC Data</h2>

          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center hover:border-purple-500 transition-colors">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload"
                disabled={isUploading || isEnriching}
              />
              <label
                htmlFor="csv-upload"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span className="text-slate-300 font-medium">
                  {isUploading ? 'Uploading...' : 'Click to upload CSV file'}
                </span>
                <span className="text-sm text-slate-500">
                  CSV should contain columns: name, address, city, state (optional: phone, email, website)
                </span>
              </label>
            </div>

            {llcRecords.length > 0 && (
              <>
                {/* Selection Controls */}
                <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-400">
                        {selectedRecords.size} of {llcRecords.length} selected
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={selectAllRecords}
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded text-sm font-medium transition-colors"
                      >
                        Select All
                      </button>
                      <button
                        onClick={deselectAllRecords}
                        disabled={selectedRecords.size === 0}
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-slate-200 rounded text-sm font-medium transition-colors"
                      >
                        Deselect All
                      </button>
                    </div>
                    <div className="flex items-center gap-2 border-l border-slate-600 pl-3">
                      <span className="text-xs text-slate-500">Quick Select:</span>
                      <button
                        onClick={() => selectByQueryStrength('high')}
                        className="px-2 py-1 bg-green-900/30 hover:bg-green-900/50 text-green-300 rounded text-xs font-medium transition-colors"
                      >
                        High ({queryStrengthStats.high})
                      </button>
                      <button
                        onClick={() => selectByQueryStrength('medium')}
                        className="px-2 py-1 bg-yellow-900/30 hover:bg-yellow-900/50 text-yellow-300 rounded text-xs font-medium transition-colors"
                      >
                        Medium ({queryStrengthStats.medium})
                      </button>
                      <button
                        onClick={() => selectByQueryStrength('low')}
                        className="px-2 py-1 bg-red-900/30 hover:bg-red-900/50 text-red-300 rounded text-xs font-medium transition-colors"
                      >
                        Low ({queryStrengthStats.low})
                      </button>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={handleEnrichSelected}
                    disabled={isEnriching || selectedRecords.size === 0}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:from-slate-700 disabled:to-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                  >
                    {isEnriching ? (
                      <>
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Enriching ({enrichmentProgress.current}/{enrichmentProgress.total})
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M13 7H7v6h6V7z" />
                          <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
                        </svg>
                        Enrich Selected ({selectedRecords.size})
                      </>
                    )}
                  </button>
                  <button
                    onClick={saveEnrichment}
                    disabled={isSaving || isEnriching}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" />
                    </svg>
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleExport}
                    disabled={isEnriching}
                    className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Export CSV
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Saved Enrichments */}
        {savedEnrichments.length > 0 && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-slate-100 mb-4">Saved Enrichments</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedEnrichments.map((enrichment) => (
                <div
                  key={enrichment.id}
                  className={`border rounded-lg p-4 transition-colors ${
                    currentEnrichmentId === enrichment.id
                      ? 'border-purple-500 bg-purple-900/20'
                      : 'border-slate-600 bg-slate-900 hover:border-slate-500'
                  }`}
                >
                  <h3 className="font-medium text-slate-100 mb-2">{enrichment.name}</h3>
                  <div className="text-sm text-slate-400 space-y-1">
                    <div>Total: {enrichment.total_records} records</div>
                    <div>Enriched: {enrichment.enriched_count} records</div>
                    <div className="text-xs text-slate-500">
                      {new Date(enrichment.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => loadEnrichment(enrichment.id)}
                      className="flex-1 px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded text-sm font-medium transition-all"
                    >
                      Load
                    </button>
                    <button
                      onClick={() => deleteEnrichment(enrichment.id)}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Statistics */}
        {llcRecords.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* State Distribution */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                Top States
              </h3>
              <div className="space-y-2">
                {stateStats.map(([state, count]) => (
                  <div key={state} className="flex items-center justify-between text-sm">
                    <span className="text-slate-300 font-medium">{state}</span>
                    <span className="text-slate-400">{count} LLCs</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Query Strength */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
                Query Strength
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-slate-300 text-sm">High</span>
                  </div>
                  <span className="text-slate-400 text-sm">{queryStrengthStats.high}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <span className="text-slate-300 text-sm">Medium</span>
                  </div>
                  <span className="text-slate-400 text-sm">{queryStrengthStats.medium}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="text-slate-300 text-sm">Low</span>
                  </div>
                  <span className="text-slate-400 text-sm">{queryStrengthStats.low}</span>
                </div>
              </div>
            </div>

            {/* Enrichment Status */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
                Enrichment Status
              </h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Pending</span>
                  <span className="text-slate-300 font-medium">{enrichmentStats.pending}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-blue-400">Processing</span>
                  <span className="text-slate-300 font-medium">{enrichmentStats.processing}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-green-400">Completed</span>
                  <span className="text-slate-300 font-medium">{enrichmentStats.completed}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-red-400">Failed</span>
                  <span className="text-slate-300 font-medium">{enrichmentStats.failed}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Records Table */}
        {llcRecords.length > 0 && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg overflow-hidden">
            <div className="p-4 border-b border-slate-700">
              <h2 className="text-xl font-semibold text-slate-100">LLC Records ({llcRecords.length})</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900">
                  <tr>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider w-12">
                      <input
                        type="checkbox"
                        checked={selectedRecords.size === llcRecords.length && llcRecords.length > 0}
                        onChange={(e) => e.target.checked ? selectAllRecords() : deselectAllRecords()}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-purple-500 focus:ring-purple-500 focus:ring-offset-slate-900"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">State</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">City</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Query Strength</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Enriched Data</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {llcRecords.map((record, idx) => (
                    <tr
                      key={idx}
                      onClick={() => toggleRecordSelection(idx)}
                      className={`cursor-pointer transition-colors ${
                        selectedRecords.has(idx)
                          ? 'bg-purple-900/20 hover:bg-purple-900/30'
                          : 'hover:bg-slate-900/50'
                      }`}
                    >
                      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedRecords.has(idx)}
                          onChange={() => toggleRecordSelection(idx)}
                          className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-purple-500 focus:ring-purple-500 focus:ring-offset-slate-900"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-100">{record.name}</td>
                      <td className="px-4 py-3 text-sm text-slate-300">{record.state || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-300">{record.city || '-'}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          record.queryStrength === 'high' ? 'bg-green-900/50 text-green-300' :
                          record.queryStrength === 'medium' ? 'bg-yellow-900/50 text-yellow-300' :
                          'bg-red-900/50 text-red-300'
                        }`}>
                          {record.queryStrength}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          record.enrichmentStatus === 'completed' ? 'bg-green-900/50 text-green-300' :
                          record.enrichmentStatus === 'processing' ? 'bg-blue-900/50 text-blue-300' :
                          record.enrichmentStatus === 'failed' ? 'bg-red-900/50 text-red-300' :
                          'bg-slate-700 text-slate-300'
                        }`}>
                          {record.enrichmentStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-400">
                        {record.enrichedData ? (
                          <div className="space-y-1">
                            {record.enrichedData.website && <div>🌐 Website</div>}
                            {record.enrichedData.email && <div>📧 Email</div>}
                            {record.enrichedData.phone && <div>📱 Phone</div>}
                            {record.enrichedData.instagram && <div>📷 Instagram</div>}
                            {record.enrichedData.facebook && <div>👤 Facebook</div>}
                            {record.enrichedData.linkedin && <div>💼 LinkedIn</div>}
                          </div>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
